"use client";

import {
  ChevronLeft,
  ChevronRight,
  Mail,
  Maximize2,
  MessagesSquare,
  Play,
  Send,
  Share2,
  Heart,
} from "lucide-react";
import Image from "next/image";
import {
  type MouseEvent,
  type ReactNode,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { A11y, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper";
import type { ProductImage } from "@/types/product";
import { buildYouTubeThumbnailUrl } from "@/lib/media/youtube";
import { FacebookIcon, WhatsAppIcon } from "@/components/UI/SocialIcons";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useOverlayManager } from "@/hooks/useOverlayManager";
import { useFavorites } from "@/hooks/useFavorites";
import { ProductImageLightbox } from "./ProductImageLightbox";
import { ProductVideoPlayer } from "./ProductVideoPlayer";
import "swiper/css";
import "swiper/css/pagination";

interface ProductGalleryProps {
  images: ProductImage[];
  productId: number;
  productName: string;
  /** Vídeo do YouTube do produto (catálogo nativo), se houver. */
  video?: { youtubeId: string } | null;
}

const fallbackImage: ProductImage = {
  src: "/images/brand/persi-materiais-eletricos-e-hidraulicos-ferramentas.webp",
  alt: "Imagem demonstrativa da Persi Materiais",
};

function subscribeToBrowserState() {
  return () => undefined;
}

export function ProductGallery({
  images,
  productId,
  productName,
  video = null,
}: ProductGalleryProps) {
  const galleryImages = images.length > 0 ? images : [fallbackImage];
  const swiperRef = useRef<SwiperInstance | null>(null);
  const shareMenuRef = useRef<HTMLDivElement>(null);
  const shareTriggerRef = useRef<HTMLButtonElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isExpandLabelVisible, setIsExpandLabelVisible] = useState(false);
  // Player do vídeo sobre a área da imagem. Só existe depois do clique na
  // miniatura/botão "Vídeo" (fachada: nada do YouTube carrega antes).
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const videoTriggerRef = useRef<HTMLButtonElement>(null);
  const mobileVideoTriggerRef = useRef<HTMLButtonElement>(null);
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(productId);
  const shareUrl = useSyncExternalStore(
    subscribeToBrowserState,
    () => window.location.href,
    () => "",
  );
  const canShareNatively = useSyncExternalStore(
    subscribeToBrowserState,
    () => typeof navigator.share === "function",
    () => false,
  );
  const hasMultipleImages = galleryImages.length > 1;
  // Miniaturas normais exibidas antes do indicador "+N" ocupar o próximo slot.
  const thumbnailLimit = 4;
  const hasHiddenThumbnails = galleryImages.length > thumbnailLimit;
  const hiddenThumbnailCount = hasHiddenThumbnails
    ? galleryImages.length - thumbnailLimit
    : 0;
  const renderedThumbnailCount = hasHiddenThumbnails
    ? thumbnailLimit + 1
    : galleryImages.length;

  function closeShareMenu() {
    setIsShareMenuOpen(false);
  }

  useOverlayManager({
    id: "product-share-menu",
    isOpen: isShareMenuOpen,
    onClose: closeShareMenu,
    returnFocusRef: shareTriggerRef,
  });
  useClickOutside({
    isOpen: isShareMenuOpen,
    refs: [shareMenuRef],
    onOutside: closeShareMenu,
  });

  function supportsImageZoom() {
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  }

  function handleZoomMove(event: MouseEvent<HTMLDivElement>) {
    if (!supportsImageZoom()) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width) * 100;
    const y = ((event.clientY - bounds.top) / bounds.height) * 100;

    setZoomOrigin({ x, y });
  }

  function handleZoomEnter() {
    if (supportsImageZoom()) setIsZoomed(true);
  }

  function handleZoomLeave() {
    setIsZoomed(false);
    setZoomOrigin({ x: 50, y: 50 });
  }

  function openVideo() {
    handleZoomLeave();
    setIsVideoOpen(true);
  }

  function closeVideo() {
    setIsVideoOpen(false);
    const trigger = window.matchMedia("(min-width: 640px)").matches
      ? videoTriggerRef.current
      : mobileVideoTriggerRef.current;
    trigger?.focus();
  }

  function selectImage(index: number) {
    setIsVideoOpen(false);
    setSelectedIndex(index);
    swiperRef.current?.slideTo(index);
    handleZoomLeave();
  }

  function showPreviousImage() {
    swiperRef.current?.slidePrev();
  }

  function showNextImage() {
    swiperRef.current?.slideNext();
  }

  function selectLightboxImage(index: number) {
    selectImage(index);
  }

  function openLightboxAtIndex(index: number) {
    selectImage(index);
    setIsLightboxOpen(true);
  }

  function handleExpandClick() {
    const usesTouchPointer = window.matchMedia(
      "(hover: none), (pointer: coarse)",
    ).matches;

    if (usesTouchPointer && !isExpandLabelVisible) {
      setIsExpandLabelVisible(true);
      return;
    }

    setIsLightboxOpen(true);
  }

  async function shareNatively() {
    if (!navigator.share || !shareUrl) return;

    try {
      await navigator.share({
        title: productName,
        text: productName,
        url: shareUrl,
      });
      setIsShareMenuOpen(false);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
    }
  }

  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(productName);
  const encodedMessage = encodeURIComponent(`${productName} ${shareUrl}`);

  return (
    <>
      <section
      aria-label={`Galeria de imagens de ${productName}`}
      onContextMenu={(event) => event.preventDefault()}
      className="relative min-w-0 max-w-full"
    >
      <div className="relative aspect-square overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Swiper
          modules={[A11y, Pagination]}
          slidesPerView={1}
          spaceBetween={0}
          allowTouchMove
          threshold={8}
          resistance
          resistanceRatio={0.85}
          rewind
          watchSlidesProgress
          pagination={hasMultipleImages ? { clickable: true } : false}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          onSlideChange={(swiper) => {
            setIsVideoOpen(false);
            setSelectedIndex(swiper.activeIndex);
            handleZoomLeave();
          }}
          className="product-gallery-pagination h-full w-full [--swiper-pagination-bullet-inactive-color:#cbd5e1] [--swiper-pagination-bullet-inactive-opacity:1] [--swiper-pagination-bullet-size:10px] [--swiper-pagination-bullet-border-radius:9999px] [--swiper-pagination-color:var(--color-secondary)]"
        >
          {galleryImages.map((image, index) => (
            <SwiperSlide key={`${image.src}-${index}`}>
              <div
                className="relative h-full w-full overflow-hidden md:cursor-zoom-in"
                onMouseEnter={handleZoomEnter}
                onMouseMove={handleZoomMove}
                onMouseLeave={handleZoomLeave}
              >
                <Image
                  src={image.src}
                  alt={image.alt}
                  fill
                  priority={index === 0}
                  sizes="(min-width: 1024px) 50vw, 100vw"
                  draggable={false}
                  onDragStart={(event) => event.preventDefault()}
                  className="select-none object-contain p-6 transition-transform duration-200 ease-out sm:p-8"
                  style={{
                    transform:
                      isZoomed && selectedIndex === index
                        ? "scale(1.8)"
                        : "scale(1)",
                    transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
                  }}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {video && isVideoOpen ? (
          <div className="absolute inset-0 z-30">
            <ProductVideoPlayer
              youtubeId={video.youtubeId}
              productName={productName}
              autoStart
              onClose={closeVideo}
            />
          </div>
        ) : null}

        {video && !isVideoOpen ? (
          <button
            ref={mobileVideoTriggerRef}
            type="button"
            onClick={openVideo}
            aria-label={`Assistir ao vídeo de ${productName}`}
            className="absolute bottom-10 right-3 z-20 inline-flex h-11 items-center gap-2 rounded-full border border-slate-100 bg-white/85 px-4 text-sm font-semibold text-foreground shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:hidden"
          >
            <Play className="h-4 w-4 fill-current text-secondary" aria-hidden="true" />
            Vídeo
          </button>
        ) : null}

        {hasMultipleImages ? (
          <div
            className="absolute left-3 top-3 z-20 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-foreground shadow-sm sm:hidden"
            aria-live="polite"
          >
            {selectedIndex + 1}/{galleryImages.length}
          </div>
        ) : null}

        <button
          type="button"
          onClick={handleExpandClick}
          onBlur={() => setIsExpandLabelVisible(false)}
          aria-label="Ampliar imagem do produto"
          aria-expanded={isExpandLabelVisible}
          title="Clique para ampliar"
          className={`group absolute bottom-10 left-3 z-20 inline-flex h-11 min-w-11 items-center overflow-hidden rounded-full border border-slate-100 bg-white/85 text-sm font-medium text-foreground shadow-md transition-[padding,gap] duration-200 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
            isExpandLabelVisible
              ? "gap-2 px-4"
              : "justify-center gap-0 px-3 hover:justify-start hover:gap-2 hover:px-4 focus-visible:justify-start focus-visible:gap-2 focus-visible:px-4"
          }`}
        >
          <Maximize2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span
            className={`overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200 motion-reduce:transition-none ${
              isExpandLabelVisible
                ? "max-w-40 opacity-100"
                : "max-w-0 opacity-0 group-hover:max-w-40 group-hover:opacity-100 group-focus-visible:max-w-40 group-focus-visible:opacity-100"
            }`}
          >
            Clique para ampliar
          </span>
        </button>

        {hasMultipleImages ? (
          <>
            <button
              type="button"
              onClick={showPreviousImage}
              aria-label="Exibir imagem anterior"
              className="absolute left-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-primary shadow-sm transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:flex"
            >
              <ChevronLeft className="h-6 w-6" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={showNextImage}
              aria-label="Exibir próxima imagem"
              className="absolute right-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-primary shadow-sm transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:flex"
            >
              <ChevronRight className="h-6 w-6" aria-hidden="true" />
            </button>
          </>
        ) : null}
      </div>

      {/* Fora da caixa overflow-hidden da imagem de propósito: o menu de
          compartilhar precisa poder crescer além do quadrado da imagem sem
          ser cortado (era clipado no mobile antes desta correção). */}
      <div className="absolute right-3 top-3 z-20 flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={() => void toggleFavorite(productId)}
          aria-pressed={favorited}
          aria-label={`${favorited ? "Remover" : "Adicionar"} ${productName} ${favorited ? "dos" : "aos"} favoritos`}
          title={favorited ? "Remover dos favoritos" : "Adicionar aos favoritos"}
          className="flex h-[39px] w-[39px] items-center justify-center rounded-xl border border-slate-300 bg-white/90 text-primary opacity-80 transition hover:border-secondary hover:text-secondary hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Heart
            aria-hidden="true"
            className={`h-[19px] w-[19px] transition-transform ${favorited ? "scale-110 fill-secondary text-secondary" : ""}`}
          />
        </button>
        <div ref={shareMenuRef} className="relative">
          <button
            ref={shareTriggerRef}
            type="button"
            onClick={() => setIsShareMenuOpen((current) => !current)}
            aria-label={`Compartilhar ${productName}`}
            aria-expanded={isShareMenuOpen}
            aria-controls="product-share-menu"
            className="flex h-[39px] w-[39px] items-center justify-center rounded-xl border border-slate-300 bg-white/90 text-primary opacity-80 transition hover:border-secondary hover:text-secondary hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <Share2 className="h-[19px] w-[19px]" aria-hidden="true" />
          </button>
          {isShareMenuOpen ? (
            <div
              id="product-share-menu"
              className="absolute right-0 mt-2 max-h-[70vh] w-52 overflow-y-auto overscroll-contain rounded-xl border border-slate-200 bg-white p-2 text-sm text-foreground shadow-lg"
            >
              {canShareNatively ? (
                <button
                  type="button"
                  onClick={() => void shareNatively()}
                  className="flex min-h-10 w-full items-center gap-3 rounded-lg px-3 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <Share2 className="h-4 w-4" aria-hidden="true" />
                  Compartilhar pelo celular
                </button>
              ) : null}
              <ShareLink
                href={`https://wa.me/?text=${encodedMessage}`}
                label="WhatsApp"
                icon={<WhatsAppIcon className="h-4 w-4" aria-hidden="true" />}
              />
              <ShareLink
                href={`sms:?body=${encodedMessage}`}
                label="Mensagens"
                icon={<MessagesSquare className="h-4 w-4" aria-hidden="true" />}
              />
              <ShareLink
                href={`mailto:?subject=${encodedText}&body=${encodedMessage}`}
                label="E-mail"
                icon={<Mail className="h-4 w-4" aria-hidden="true" />}
              />
              <ShareLink
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                label="Facebook"
                icon={<FacebookIcon className="h-4 w-4" aria-hidden="true" />}
              />
              <ShareLink
                href={`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`}
                label="Telegram"
                icon={<Send className="h-4 w-4" aria-hidden="true" />}
              />
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-3 hidden min-w-0 max-w-full gap-3 overflow-x-auto pb-2 sm:flex">
        {galleryImages.slice(0, renderedThumbnailCount).map((image, index) => {
          const isSelected = !isVideoOpen && index === selectedIndex;
          const isOverflowSlot = hasHiddenThumbnails && index === thumbnailLimit;

          if (isOverflowSlot) {
            return (
              <button
                key={`${image.src}-${index}`}
                type="button"
                onClick={() => openLightboxAtIndex(index)}
                aria-label={`Ver mais ${hiddenThumbnailCount} ${hiddenThumbnailCount === 1 ? "imagem" : "imagens"} de ${productName}`}
                className="relative aspect-square w-20 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white transition-colors hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <Image
                  src={image.src}
                  alt=""
                  fill
                  sizes="80px"
                  draggable={false}
                  onDragStart={(event) => event.preventDefault()}
                  className="select-none object-contain p-2"
                />
                <span className="absolute inset-0 flex items-center justify-center bg-slate-950/60 text-base font-semibold text-white">
                  +{hiddenThumbnailCount}
                </span>
              </button>
            );
          }

          return (
            <button
              key={`${image.src}-${index}`}
              type="button"
              onClick={() => selectImage(index)}
              aria-label={`Exibir imagem ${index + 1} de ${productName}`}
              aria-pressed={isSelected}
              className={`relative aspect-square w-20 shrink-0 overflow-hidden rounded-xl border bg-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                isSelected
                  ? "border-primary"
                  : "border-slate-200 hover:border-slate-400"
              }`}
            >
              <Image
                src={image.src}
                alt=""
                fill
                sizes="80px"
                draggable={false}
                onDragStart={(event) => event.preventDefault()}
                className="select-none object-contain p-2"
              />
            </button>
          );
        })}
        {video ? (
          <button
            ref={videoTriggerRef}
            type="button"
            onClick={openVideo}
            aria-label={`Assistir ao vídeo de ${productName}`}
            aria-pressed={isVideoOpen}
            className={`relative aspect-square w-20 shrink-0 overflow-hidden rounded-xl border bg-slate-950 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
              isVideoOpen ? "border-primary" : "border-slate-200 hover:border-slate-400"
            }`}
          >
            <Image
              src={buildYouTubeThumbnailUrl(video.youtubeId)}
              alt=""
              fill
              sizes="80px"
              draggable={false}
              className="select-none object-cover opacity-90"
            />
            <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-slate-950/30 text-xs font-semibold text-white">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary">
                <Play className="ml-0.5 h-4 w-4 fill-current" aria-hidden="true" />
              </span>
              Vídeo
            </span>
          </button>
        ) : null}
      </div>
      </section>
      {isLightboxOpen ? (
        <ProductImageLightbox
          images={galleryImages}
          selectedIndex={selectedIndex}
          productName={productName}
          onIndexChange={selectLightboxImage}
          onClose={() => setIsLightboxOpen(false)}
        />
      ) : null}
    </>
  );
}

interface ShareLinkProps {
  href: string;
  label: string;
  icon: ReactNode;
}

function ShareLink({ href, label, icon }: ShareLinkProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex min-h-10 items-center gap-3 rounded-lg px-3 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {icon}
      {label}
    </a>
  );
}
